import { keepPreviousData } from "@tanstack/react-query";
import { addDays } from "date-fns";
import type { Identifier } from "ra-core";
import { useGetIdentity, useGetList } from "ra-core";
import { useMemo } from "react";

import type { CalendarItem } from "@/components/fretum/types";
import { isInternalTask } from "../tasks/taskKind";
import type { Task } from "../types";
import { formatAgendaDate } from "./agendaModel";
import { agendaHref, dayKey, isAllDay, toLocalDate } from "./mobileModel";

export type AgendaEntry = {
  id: string;
  title: string;
  start: Date;
  end: Date | null;
  allDay: boolean;
  source: string;
  done: boolean;
  contact_id?: Identifier | null;
  assignee_id?: Identifier | null;
  /** Internal team task of the `tasks` table (no contact). */
  internal?: boolean;
  href: string | null;
};

export const SOURCE_BAR: Record<string, string> = {
  task: "bg-chart-3",
  event: "bg-chart-1",
  team_task: "bg-chart-4",
  deal_closing: "bg-chart-2",
  milestone: "bg-chart-5",
  renewal: "bg-muted-foreground",
  invoice_due: "bg-destructive",
};

/**
 * Calendar items plus the user's own tasks between `start` and `end`
 * (inclusive, local days), sorted by start time. Same sources the agenda has
 * always shown; only the window changes with the view.
 */
export const useAgendaEntries = (start: Date, end: Date) => {
  const { identity } = useGetIdentity();
  const startKey = formatAgendaDate(start);
  const endKey = formatAgendaDate(end);

  const calendar = useGetList<CalendarItem>(
    "calendar_items",
    {
      filter: {
        "start_at@gte": start.toISOString(),
        "start_at@lte": addDays(end, 1).toISOString(),
      },
      pagination: { page: 1, perPage: 500 },
      sort: { field: "start_at", order: "ASC" },
    },
    // Keep the previous window on screen while the next one loads.
    { retry: false, placeholderData: keepPreviousData },
  );
  const tasks = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "due_date", order: "ASC" },
      filter: { sales_id: identity?.id },
    },
    { enabled: identity?.id != null },
  );

  const entries = useMemo(() => {
    const fromCalendar: AgendaEntry[] = (calendar.data ?? [])
      .filter((item) => item.source !== "task")
      .map((item) => ({
        id: String(item.id),
        title: item.title,
        start: toLocalDate(item.start_at),
        end: item.end_at ? toLocalDate(item.end_at) : null,
        allDay: item.all_day || isAllDay(item.start_at),
        source: item.source,
        done: item.status === "done",
        contact_id: item.contact_id,
        assignee_id: item.assignee_id,
        href: agendaHref(item),
      }));
    const fromTasks: AgendaEntry[] = (tasks.data ?? [])
      .filter((task) => task.due_date)
      .map((task) => ({
        id: `task-${task.id}`,
        title: task.text,
        start: toLocalDate(task.due_date),
        end: null,
        allDay: isAllDay(task.due_date),
        source: "task",
        done: task.done_date != null,
        contact_id: task.contact_id,
        assignee_id: task.sales_id,
        internal: isInternalTask(task),
        href: agendaHref({ source: "task", contact_id: task.contact_id }),
      }));
    return [...fromCalendar, ...fromTasks]
      .filter((entry) => {
        const key = dayKey(entry.start);
        return key >= startKey && key <= endKey;
      })
      .sort(
        (left, right) =>
          Number(right.allDay) - Number(left.allDay) ||
          left.start.getTime() - right.start.getTime(),
      );
  }, [calendar.data, tasks.data, startKey, endKey]);

  const pending = (calendar.isPending && !calendar.data) || tasks.isPending;
  return { entries, pending };
};
